import { useMemo, useState, useEffect } from 'react'
import { normalizeAllResponses, calculateMatchScore } from '../engine/responseNormalizer.js'
import { calculateDimensionMatches, identifyLowMatches, calculateOverallMatch } from '../engine/matchCalculator.js'
import { generateFeedback, generateGeneralFeedback } from '../engine/feedbackGenerator.js'
import { getCurrentUser, checkPremiumStatus } from '../services/firebaseService'
import { doc, collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../config/firebase'
import { translateDimension, translateLabel } from '../utils/translations.js'
import '../styles/components/results-display.css'

function ResultsDisplayV2({ data, onStartOver }) {
  const [user, setUser] = useState(null)
  const [hasPremium, setHasPremium] = useState(false)
  const [checkingPremium, setCheckingPremium] = useState(true)
  const [savingEvaluation, setSavingEvaluation] = useState(false)
  const [evaluationSaved, setEvaluationSaved] = useState(false)

  useEffect(() => {
    const currentUser = getCurrentUser()
    setUser(currentUser)
    if (currentUser) {
      checkPremiumStatus(currentUser.uid).then(premium => {
        setHasPremium(premium)
        setCheckingPremium(false)
      })
    } else {
      setCheckingPremium(false)
    }
  }, [])

  if (!data) {
    return (
      <div className="results-display step active" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '3em', marginBottom: '16px' }}>📊</div>
        <h2>Ingen data at vise</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
          Der er ingen resultater at vise. Prøv at starte forfra.
        </p>
        {onStartOver && (
          <button className="btn" onClick={onStartOver}>
            Start forfra
          </button>
        )}
      </div>
    )
  }

  const { preferences = [], responses = {}, booking, bookingResponse, alternativeBooking, topicData } = data

  // Check if sender responses are available (for comparison)
  const senderResponsesData = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('parkort_sender_responses')
      return stored ? JSON.parse(stored) : null
    } catch (e) {
      return null
    }
  }, [])

  // Calculate matches if we have topic data
  const matchResults = useMemo(() => {
    if (!topicData || !preferences.length || !responses || Object.keys(responses).length === 0) {
      return null
    }

    // Get items from topic data
    const items = []
    preferences.forEach(pref => {
      if (pref.topicId && topicData[pref.topicId]) {
        const item = topicData[pref.topicId].items.find(i => i.id === pref.id)
        if (item) {
          items.push({ ...item, ...pref })
        }
      }
    })

    if (items.length === 0) {
      return null
    }

    // Normalize responses (recipient's responses)
    const normalizedResponses = normalizeAllResponses(responses, items)
    
    // If sender has responded, compare both responses
    if (senderResponsesData && Object.keys(senderResponsesData).length > 0) {
      // Normalize sender responses
      const normalizedSenderResponses = normalizeAllResponses(senderResponsesData, items)
      
      // Calculate match between sender and recipient
      const dimensionMatches = calculateDimensionMatches(
        normalizedSenderResponses,
        normalizedResponses,
        items
      )
      
      const overallMatch = calculateOverallMatch(dimensionMatches)
      const lowMatches = identifyLowMatches(dimensionMatches)
      
      // Generate feedback
      const allFeedbackRules = []
      preferences.forEach(pref => {
        if (pref.topicId && topicData[pref.topicId]?.topic?.feedbackRules) {
          allFeedbackRules.push(...topicData[pref.topicId].topic.feedbackRules)
        }
      })
      const feedback = generateFeedback(allFeedbackRules, dimensionMatches, lowMatches)
      const generalFeedback = generateGeneralFeedback(dimensionMatches, lowMatches)

      return {
        dimensionMatches,
        lowMatches,
        feedback,
        generalFeedback,
        overallMatch,
        senderResponses: normalizedSenderResponses,
        recipientResponses: normalizedResponses,
        isComparison: true
      }
    }
    
    // Fallback: If no sender responses, show recipient's own scores (old behavior)
    const dimensionScores = {}
    items.forEach(item => {
      if (!item.dimension) return
      
      if (!dimensionScores[item.dimension]) {
        dimensionScores[item.dimension] = []
      }
      
      const score = normalizedResponses[item.id]
      if (score !== undefined) {
        dimensionScores[item.dimension].push(score)
      }
    })

    // Calculate average score per dimension (single person view)
    const dimensionMatches = {}
    Object.entries(dimensionScores).forEach(([dimension, scores]) => {
      if (scores.length > 0) {
        const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length
        dimensionMatches[dimension] = {
          score: avgScore,
          level: avgScore >= 0.8 ? 'high' : avgScore >= 0.6 ? 'medium' : 'low',
          diff: 1 - avgScore
        }
      }
    })

    const lowMatches = identifyLowMatches(dimensionMatches)
    
    // Generate feedback
    const allFeedbackRules = []
    preferences.forEach(pref => {
      if (pref.topicId && topicData[pref.topicId]?.topic?.feedbackRules) {
        allFeedbackRules.push(...topicData[pref.topicId].topic.feedbackRules)
      }
    })
    const feedback = generateFeedback(allFeedbackRules, dimensionMatches, lowMatches)
    const generalFeedback = generateGeneralFeedback(dimensionMatches, lowMatches)

    return {
      dimensionMatches,
      lowMatches,
      feedback,
      generalFeedback,
      overallMatch: Object.keys(dimensionMatches).length > 0
        ? Object.values(dimensionMatches).reduce((a, b) => {
            const scoreA = typeof a === 'object' ? a.score : a
            const scoreB = typeof b === 'object' ? b.score : b
            return scoreA + scoreB
          }, 0) / Object.keys(dimensionMatches).length
        : 0
    }
  }, [data, preferences, responses, topicData])

  // Manual save evaluation function
  const handleSaveEvaluation = async () => {
    if (!user || !matchResults || savingEvaluation) return
    
    setSavingEvaluation(true)
    try {
      // Get module names from preferences
      const modules = [...new Set(data.preferences?.map(p => {
        if (p.topicId && data.topicData?.[p.topicId]) {
          return data.topicData[p.topicId].topic?.name || p.topicId
        }
        return null
      }).filter(Boolean))]

      const evaluationData = {
        userId: user.uid,
        title: `Evaluering ${new Date().toLocaleDateString('da-DK')}`,
        preferences: data.preferences,
        responses: data.responses,
        matchResults: matchResults,
        generalFeedback: matchResults.generalFeedback, // Include generalFeedback
        modules: modules,
        overallMatch: matchResults.overallMatch,
        createdAt: serverTimestamp()
      }

      // Debug: Log what we're saving
      console.log('💾 Saving evaluation with generalFeedback:', {
        hasGeneralFeedback: !!matchResults.generalFeedback,
        generalFeedbackKeys: matchResults.generalFeedback ? Object.keys(matchResults.generalFeedback) : [],
        summary: matchResults.generalFeedback?.summary,
        insightsCount: matchResults.generalFeedback?.insights?.length || 0,
        actionItemsCount: matchResults.generalFeedback?.actionItems?.length || 0
      })

      const docRef = await addDoc(collection(db, 'evaluations'), evaluationData)
      console.log('✅ Evaluation saved with ID:', docRef.id)
      setEvaluationSaved(true)
      
      // Show success message
      setTimeout(() => {
        setEvaluationSaved(false)
      }, 3000)
    } catch (error) {
      console.error('Error saving evaluation:', error)
      alert('Fejl ved gemning: ' + error.message)
    } finally {
      setSavingEvaluation(false)
    }
  }

  // Auto-save evaluation if user is logged in (premium or not)
  useEffect(() => {
    if (matchResults && user && matchResults.overallMatch !== undefined && !evaluationSaved) {
      const saveEvaluation = async () => {
        try {
          // Get module names from preferences
          const modules = [...new Set(data.preferences?.map(p => {
            if (p.topicId && data.topicData?.[p.topicId]) {
              return data.topicData[p.topicId].topic?.name || p.topicId
            }
            return null
          }).filter(Boolean))]

          const evaluationData = {
            userId: user.uid,
            title: `Evaluering ${new Date().toLocaleDateString('da-DK')}`,
            preferences: data.preferences,
            responses: data.responses,
            matchResults: matchResults,
            generalFeedback: matchResults.generalFeedback, // Include generalFeedback
            modules: modules,
            overallMatch: matchResults.overallMatch,
            createdAt: serverTimestamp()
          }

          // Debug: Log what we're auto-saving
          console.log('💾 Auto-saving evaluation with generalFeedback:', {
            hasGeneralFeedback: !!matchResults.generalFeedback,
            generalFeedbackKeys: matchResults.generalFeedback ? Object.keys(matchResults.generalFeedback) : [],
            summary: matchResults.generalFeedback?.summary,
            insightsCount: matchResults.generalFeedback?.insights?.length || 0,
            actionItemsCount: matchResults.generalFeedback?.actionItems?.length || 0
          })

          const docRef = await addDoc(collection(db, 'evaluations'), evaluationData)
          console.log('✅ Evaluation auto-saved with ID:', docRef.id)
          setEvaluationSaved(true)
        } catch (error) {
          console.error('Error auto-saving evaluation:', error)
        }
      }

      // Debounce save - wait 2 seconds after matchResults is ready
      const timeoutId = setTimeout(saveEvaluation, 2000)
      return () => clearTimeout(timeoutId)
    }
  }, [matchResults, user, data, evaluationSaved])

  const getResponseEmoji = (response) => {
    if (typeof response === 'string') {
      switch (response) {
        case 'yes': case '3': case '4': return '✅'
        case 'maybe': case '1': case '2': return '🤔'
        case 'no': case '0': return '❌'
        default: return '⚪'
      }
    }
    return '⚪'
  }

  const getResponseText = (response) => {
    if (typeof response === 'string') {
      if (response === 'yes') return 'Ja'
      if (response === 'maybe') return 'Måske'
      if (response === 'no') return 'Nej'
      if (response === '3') return 'Meget vigtigt'
      if (response === '2') return 'Vigtigt'
      if (response === '1') return 'Lidt vigtigt'
      if (response === '0') return 'Ligegyldigt'
      if (response === '4') return 'Altid'
      if (response === '3') return 'Ofte'
      if (response === '2') return 'Nogle gange'
      if (response === '1') return 'Sjældent'
    }
    return 'Ikke besvaret'
  }

  return (
    <div className="results-display step active">
      <h2>💕 Dine svar er modtaget!</h2>
      
      {/* Save Evaluation Button - Explicit */}
      {user && matchResults && (
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          background: evaluationSaved ? 'rgba(81, 207, 102, 0.1)' : 'rgba(102, 126, 234, 0.1)',
          borderRadius: '12px',
          border: `2px solid ${evaluationSaved ? 'rgba(81, 207, 102, 0.3)' : 'rgba(102, 126, 234, 0.3)'}`,
          textAlign: 'center'
        }}>
          {evaluationSaved ? (
            <>
              <div style={{ fontSize: '2em', marginBottom: '8px' }}>✅</div>
              <p style={{ margin: 0, color: '#51cf66', fontWeight: '600' }}>
                Evaluering gemt! Gå til Dashboard for at se den.
              </p>
            </>
          ) : (
            <>
              <p style={{ marginBottom: '12px', color: 'var(--text-primary)' }}>
                💾 Gem denne evaluering i dit Dashboard
              </p>
              <button
                className="btn"
                onClick={handleSaveEvaluation}
                disabled={savingEvaluation}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  minHeight: '48px',
                  padding: '12px 24px'
                }}
              >
                {savingEvaluation ? 'Gemmer...' : '💾 Gem Evaluering'}
              </button>
            </>
          )}
        </div>
      )}

      {matchResults && (
        <div className="results-summary">
          <h3>Match Oversigt</h3>
          <div className="match-stats">
            <div className="stat-item">
              <span className="stat-value">{(matchResults.overallMatch * 100).toFixed(0)}%</span>
              <span className="stat-label">{translateLabel('Overall Match')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{matchResults.lowMatches.length}</span>
              <span className="stat-label">{translateLabel('Areas for Growth')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{matchResults.feedback.length}</span>
              <span className="stat-label">{translateLabel('Feedback Items')}</span>
            </div>
          </div>

          {matchResults.generalFeedback && (
            <div style={{ marginTop: '20px' }}>
              {/* Dynamic Summary */}
              {matchResults.generalFeedback.summary && (
                <div style={{ 
                  padding: '20px', 
                  background: 'rgba(102, 126, 234, 0.1)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(102, 126, 234, 0.3)',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ marginBottom: '12px', color: 'var(--cyber-blue)' }}>📊 Din Evaluering</h4>
                  <p style={{ fontSize: '1.05em', lineHeight: '1.7' }}>{matchResults.generalFeedback.summary}</p>
                </div>
              )}
              
              {/* Insights */}
              {matchResults.generalFeedback.insights && matchResults.generalFeedback.insights.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <h4 style={{ marginBottom: '16px' }}>💡 Insigter</h4>
                  {matchResults.generalFeedback.insights.map((insight, index) => (
                    <div 
                      key={index}
                      style={{
                        padding: '16px',
                        marginBottom: '12px',
                        background: insight.type === 'strength' 
                          ? 'rgba(81, 207, 102, 0.1)' 
                          : insight.type === 'growth' || insight.type === 'attention'
                          ? 'rgba(255, 193, 7, 0.1)'
                          : 'rgba(102, 126, 234, 0.1)',
                        border: `1px solid ${
                          insight.type === 'strength' 
                            ? 'rgba(81, 207, 102, 0.3)' 
                            : insight.type === 'growth' || insight.type === 'attention'
                            ? 'rgba(255, 193, 7, 0.3)'
                            : 'rgba(102, 126, 234, 0.3)'
                        }`,
                        borderRadius: '8px'
                      }}
                    >
                      <h5 style={{ 
                        marginBottom: '8px',
                        color: insight.type === 'strength' 
                          ? '#51cf66' 
                          : insight.type === 'growth' || insight.type === 'attention'
                          ? '#FFC107'
                          : 'var(--cyber-blue)'
                      }}>
                        {insight.title}
                      </h5>
                      <p style={{ margin: 0, fontSize: '0.95em', lineHeight: '1.6' }}>
                        {insight.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Action Items */}
              {matchResults.generalFeedback.actionItems && matchResults.generalFeedback.actionItems.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h4 style={{ marginBottom: '16px' }}>🎯 Næste Skridt</h4>
                  {matchResults.generalFeedback.actionItems
                    .sort((a, b) => {
                      const priorityOrder = { high: 3, medium: 2, low: 1 }
                      return (priorityOrder[b.priority] || 2) - (priorityOrder[a.priority] || 2)
                    })
                    .map((item, index) => (
                    <div 
                      key={index}
                      style={{
                        padding: '14px',
                        marginBottom: '10px',
                        background: item.priority === 'high' 
                          ? 'rgba(255, 107, 107, 0.1)' 
                          : item.priority === 'medium'
                          ? 'rgba(255, 193, 7, 0.1)'
                          : 'rgba(102, 126, 234, 0.1)',
                        borderLeft: `4px solid ${
                          item.priority === 'high' 
                            ? '#ff6b6b' 
                            : item.priority === 'medium'
                            ? '#FFC107'
                            : 'var(--cyber-blue)'
                        }`,
                        borderRadius: '6px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
                        <span style={{ fontSize: '1.2em' }}>
                          {item.priority === 'high' ? '🔴' : item.priority === 'medium' ? '🟡' : '🔵'}
                        </span>
                        <p style={{ margin: 0, flex: 1, fontSize: '0.95em', lineHeight: '1.6' }}>
                          {item.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {matchResults && matchResults.dimensionMatches && Object.keys(matchResults.dimensionMatches).length > 0 && (
        <div className="results-summary" style={{ marginTop: '30px' }}>
          <h3>{translateLabel('Match per Dimension')}</h3>
          <p style={{ fontSize: '0.9em', opacity: 0.8, marginBottom: '20px' }}>
            Brug disse scores til at identificere områder hvor I matcher godt og områder til udvikling. Klik på et område for at se detaljer.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px', marginTop: '15px' }}>
            {Object.entries(matchResults.dimensionMatches)
              .sort(([, a], [, b]) => {
                const scoreA = typeof a === 'object' ? a.score : a
                const scoreB = typeof b === 'object' ? b.score : b
                return scoreA - scoreB // Sort by score (lowest first - areas for growth first)
              })
              .map(([dimension, match]) => {
                const score = typeof match === 'object' ? match.score : match
                const level = typeof match === 'object' ? match.level : (score >= 0.8 ? 'high' : score >= 0.6 ? 'medium' : 'low')
                const percent = (score * 100).toFixed(0)
                const color = level === 'high' ? '#48bb78' : level === 'medium' ? '#FFD700' : '#f5576c'
                const readableName = translateDimension(dimension)
                
                // Generate insight for this dimension
                let insight = ''
                if (score >= 0.8) {
                  insight = 'I er enige her - brug dette som styrke'
                } else if (score >= 0.6) {
                  insight = 'I er nogenlunde enige - kan forbedres'
                } else {
                  insight = 'Forskelle identificeret - godt samtaleemne'
                }
                
                return (
                  <div 
                    key={dimension} 
                    role="button"
                    tabIndex={0}
                    aria-label={`${readableName}: ${percent}% match - ${insight}`}
                    style={{ 
                      padding: '16px', 
                      background: 'rgba(255,255,255,0.05)', 
                      borderRadius: '8px',
                      border: `2px solid ${color}40`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      minHeight: '120px' /* Ensure readable on mobile */
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.08)'
                      e.currentTarget.style.transform = 'translateY(-2px)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        // Could add expand/collapse functionality here
                      }
                    }}
                  >
                    <div style={{ fontSize: '0.85em', opacity: 0.8, marginBottom: '8px', fontWeight: '600' }}>
                      {readableName}
                    </div>
                    <div style={{ fontSize: '2em', fontWeight: 'bold', color, marginBottom: '4px' }}>
                      {percent}%
                    </div>
                    <div style={{ fontSize: '0.75em', opacity: 0.7, marginBottom: '8px' }}>
                      {level === 'high' ? 'Høj match' : level === 'medium' ? 'Medium match' : 'Lav match'}
                    </div>
                    <div style={{ 
                      fontSize: '0.8em', 
                      opacity: 0.8, 
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255,255,255,0.1)',
                      fontStyle: 'italic'
                    }}>
                      {insight}
                    </div>
                  </div>
                )
              })}
          </div>
          
          {/* How to use this section */}
          <div style={{ 
            marginTop: '24px', 
            padding: '16px', 
            background: 'rgba(102, 126, 234, 0.1)', 
            borderRadius: '8px',
            border: '1px solid rgba(102, 126, 234, 0.3)'
          }}>
            <h4 style={{ marginBottom: '8px', fontSize: '1em' }}>💡 {translateLabel('How to Use This')}</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9em', lineHeight: '1.7' }}>
              <li><strong>Høje scores (grønne):</strong> Dette er jeres styrker - områder hvor I matcher godt. Brug dem som fundament.</li>
              <li><strong>Lave scores (røde):</strong> Dette er områder til udvikling - start samtaler her for at forstå hinanden bedre.</li>
              <li><strong>Medium scores (gule):</strong> I er nogenlunde enige - kan forbedres gennem dialog.</li>
            </ul>
          </div>
        </div>
      )}

      {matchResults && matchResults.feedback && matchResults.feedback.length > 0 && (
        <div className="results-summary" style={{ marginTop: '20px' }}>
          <h3>💡 Feedback & Næste Skridt</h3>
          {matchResults.feedback.map((fb, index) => {
            // Translate dimension names
            return (
              <div key={index} className="feedback" style={{ marginTop: '15px' }}>
                <div className="feedback-title">{fb.title}</div>
                <p>{fb.prompt}</p>
                {fb.suggestions && fb.suggestions.length > 0 && (
                  <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                    {fb.suggestions.map((suggestion, i) => (
                      <li key={i} style={{ margin: '5px 0' }}>{suggestion}</li>
                    ))}
                  </ul>
                )}
                <small style={{ opacity: 0.7, display: 'block', marginTop: '10px' }}>
                  Dimension: {translateDimension(fb.dimension)} • Match: {((fb.matchScore || 0) * 100).toFixed(1)}%
                </small>
              </div>
            )
          })}
        </div>
      )}

      <div className="results-list" style={{ marginTop: '30px' }}>
        <h3>Detaljerede Svar</h3>
        {preferences.map((pref, index) => {
          const itemId = pref.id || index
          const response = responses[itemId]
          return (
            <div key={itemId} className="result-item">
              <div className="result-question">{pref.text || pref}</div>
              <div className={`result-answer ${response || 'pending'}`}>
                {getResponseEmoji(response)} {getResponseText(response)}
              </div>
            </div>
          )
        })}
      </div>

      {booking && (
        <div className="booking-result">
          <h3>📅 Booking</h3>
          {bookingResponse === 'accept' && (
            <p className="booking-accepted">
              ✅ Accepteret: {new Date(`${booking.date}T${booking.time}`).toLocaleDateString('da-DK', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          )}
          {bookingResponse === 'alternative' && alternativeBooking && (
            <p className="booking-alternative">
              📅 Alternativt forslag: {new Date(`${alternativeBooking.date}T${alternativeBooking.time}`).toLocaleDateString('da-DK', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          )}
        </div>
      )}

      {/* Premium Thank You / Upgrade Prompt */}
      {!checkingPremium && !hasPremium && (
        // Non-premium user - show upgrade prompt
        <div className="premium-upgrade-prompt" style={{
          marginTop: '40px',
          padding: '24px',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(102, 126, 234, 0.3)',
          textAlign: 'center'
        }}>
          <h3 style={{ marginBottom: '12px', fontSize: '1.3em' }}>
            💎 Få endnu mere ud af Parkort
          </h3>
          <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Med Premium får du adgang til alle moduler, dybere analyse og mulighed for at gemme resultater.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href={import.meta.env.VITE_STRIPE_PAYMENT_LINK_MONTHLY || 'https://buy.stripe.com/28EdR8dhk2fN4Evbaj7kc02'}
              target="_blank"
              rel="noopener noreferrer"
              className="btn"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textDecoration: 'none',
                display: 'inline-block'
              }}
            >
              Prøv Premium (19 kr/måned)
            </a>
            <button 
              className="btn btn-outline"
              onClick={onStartOver}
              style={{
                border: '2px solid var(--cyber-blue)',
                color: 'var(--cyber-blue)',
                background: 'transparent'
              }}
            >
              Lav nyt kort
            </button>
          </div>
        </div>
      )}
      
      {/* Premium User Thank You */}
      {!checkingPremium && hasPremium && (
        <div className="premium-thank-you" style={{
          marginTop: '40px',
          padding: '24px',
          background: 'linear-gradient(135deg, rgba(81, 207, 102, 0.15) 0%, rgba(102, 126, 234, 0.15) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(81, 207, 102, 0.3)',
          textAlign: 'center'
        }}>
          <h3 style={{ marginBottom: '12px', fontSize: '1.3em' }}>
            ✨ Tak for din support!
          </h3>
          <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
            {user ? 'Din evaluering er gemt automatisk i dit dashboard.' : 'Log ind for at gemme evalueringer.'}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {user && (
              <button 
                className="btn"
                onClick={() => window.location.href = '/dist/?dashboard=true'}
                style={{
                  background: 'linear-gradient(135deg, #51cf66 0%, #667eea 100%)',
                  color: 'white'
                }}
              >
                📊 Gå til Dashboard
              </button>
            )}
            <button 
              className="btn btn-outline"
              onClick={onStartOver}
              style={{
                border: '2px solid var(--cyber-blue)',
                color: 'var(--cyber-blue)',
                background: 'transparent'
              }}
            >
              Lav nyt kort
            </button>
          </div>
        </div>
      )}

    </div>
  )
}

export default ResultsDisplayV2
